import * as Sentry from "@sentry/browser";

import { getCookie } from "../../utils";

type LogErrorOptions = {
    sendToSlack?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    context?: Record<string, any>;
    tags?: Record<string, string>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const postErrorReport = (error: any, opts: LogErrorOptions, sentryCaptured: boolean) => {
    const e = error instanceof Error ? error : new Error(error);
    fetch("/events/error/", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRFToken": getCookie("csrftoken") ?? "",
        },
        body: JSON.stringify({
            error_message: e.message || "Unknown error",
            error_stack: e.stack || e.toString(),
            send_to_slack: opts.sendToSlack,
            context: opts.context,
            tags: opts.tags,
            sentry_captured: sentryCaptured,
        }),
    }).catch(() => {
        // Silently ignore - if error logging fails, there's nothing more we can do.
    });
};

/**
 * Error logging function for the UI. Captures to Sentry when the browser client is
 * initialized, and always posts to /events/error/ so the server's structured-log
 * mirror sees every reported error. When Sentry already captured client-side, the
 * post carries sentry_captured so the server does not relay a duplicate event.
 *
 * @param {Error} error error object that needs to be logged
 * @param {LogErrorOptions} opts optional configuration
 * @param {boolean} opts.sendToSlack whether to send error to Slack
 * @param {Object} opts.context additional context data to attach to Sentry error
 * @param {Record<string, string>} opts.tags additional tags to attach to Sentry error
 *
 * @example
 * logError("Something went wrong", { sendToSlack: true });
 * logError("WebSocket error", { context: { url: wsUrl, readyState: 3 }, tags: { error_type: "websocket" } });
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const logError = (error: any, opts: LogErrorOptions = {}) => {
    const client = Sentry.getClient();
    if (client) {
        Sentry.withScope((scope) => {
            if (opts.context && Object.keys(opts.context).length > 0) {
                Object.entries(opts.context).forEach(([key, value]) => {
                    scope.setContext(key, value);
                });
            }
            if (opts.tags && Object.keys(opts.tags).length > 0) {
                scope.setTags(opts.tags);
            }
            Sentry.captureException(error);
        });
        postErrorReport(error, opts, true);
    } else {
        postErrorReport(error, opts, false);
        console.error(error);
    }
};
